import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  BookOpen, 
  Plus, 
  ChevronRight,
  ChevronDown,
  Edit3, 
  CheckCircle2,
  Clock,
  Trash2,
  ThumbsUp,
  FileText,
  Users,
  Timer,
  MessageSquare
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "react-hot-toast";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

const formatWait = (seconds) => {
  if (!seconds || seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return `${Math.floor(seconds / 604800)}w`;
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return '—'; }
};

// ─── Student Journal Card (teacher view) ─────────────────────────────────────

function StudentJournalCard({ student, onAcknowledge, onNavigate, isExpanded, onToggle }) {
  const [reviewedExpanded, setReviewedExpanded] = useState(false);

  // Visible section: still awaiting review, OR already reviewed but has an unseen
  // follow-up comment — these resurface out of the collapsed "reviewed" list.
  const pendingJournals = student.journals.filter(j => j.status === 'submitted' || j.has_new_comment);
  const reviewedJournals = student.journals.filter(j => j.status === 'reviewed' && !j.has_new_comment);

  return (
    <Card
      data-testid={`student-card-${student.student_id}`}
      className="border-slate-100 overflow-hidden transition-shadow hover:shadow-md"
    >
      {/* Card Header */}
      <button
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50/60 transition-colors"
        onClick={onToggle}
        data-testid={`toggle-student-${student.student_id}`}
      >
        <Avatar className="w-11 h-11 flex-shrink-0">
          <AvatarImage src={student.student_picture} />
          <AvatarFallback className="bg-amber-100 text-amber-700 font-semibold">
            {getInitials(student.student_name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 text-base leading-tight">
            {student.student_name}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {student.total_count} {student.total_count === 1 ? 'entry' : 'entries'}
            </span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              avg {student.avg_word_count} words
            </span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              last {timeAgo(student.last_submitted_at)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {student.pending_count > 0 && (
            <Badge
              data-testid={`pending-badge-${student.student_id}`}
              className="bg-amber-500 hover:bg-amber-500 text-white text-xs px-2"
            >
              {student.pending_count} pending
            </Badge>
          )}
          {student.new_comment_count > 0 && (
            <Badge
              data-testid={`new-comment-badge-${student.student_id}`}
              className="bg-sky-500 hover:bg-sky-500 text-white text-xs px-2 gap-1"
            >
              <MessageSquare className="w-3 h-3" />
              {student.new_comment_count} new comment{student.new_comment_count > 1 ? 's' : ''}
            </Badge>
          )}
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Expandable journal list */}
      {isExpanded && (
        <div className="border-t border-slate-100">
          {student.journals.length === 0 ? (
            <p className="text-sm text-slate-400 px-5 py-4">No entries yet.</p>
          ) : (
            <>
              {/* Pending journals — always visible */}
              {pendingJournals.length > 0 && (
                <div className="divide-y divide-slate-50">
                  {pendingJournals.map(journal => (
                    <JournalRow
                      key={journal.journal_id}
                      journal={journal}
                      onAcknowledge={onAcknowledge}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              )}

              {/* Reviewed journals — collapsed by default */}
              {reviewedJournals.length > 0 && (
                <div className={pendingJournals.length > 0 ? 'border-t border-slate-100' : ''}>
                  <button
                    data-testid={`toggle-reviewed-${student.student_id}`}
                    className="w-full flex items-center justify-between px-5 py-2.5 text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
                    onClick={(e) => { e.stopPropagation(); setReviewedExpanded(v => !v); }}
                  >
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      {reviewedJournals.length} reviewed
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${reviewedExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {reviewedExpanded && (
                    <div className="divide-y divide-slate-50 border-t border-slate-50">
                      {reviewedJournals.map(journal => (
                        <JournalRow
                          key={journal.journal_id}
                          journal={journal}
                          onAcknowledge={onAcknowledge}
                          onNavigate={onNavigate}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* No pending, no reviewed toggle needed */}
              {pendingJournals.length === 0 && reviewedJournals.length === 0 && (
                <p className="text-sm text-slate-400 px-5 py-4">No entries yet.</p>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Individual journal row inside student card ───────────────────────────────

function JournalRow({ journal, onAcknowledge, onNavigate }) {
  const isPending = journal.status === 'submitted';
  const isReviewed = journal.status === 'reviewed';
  const hasNewComment = journal.has_new_comment;

  return (
    <div
      data-testid={`journal-row-${journal.journal_id}`}
      className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer group transition-colors"
      onClick={() => onNavigate(journal.journal_id)}
    >
      {/* Status indicator dot */}
      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-0.5 ${hasNewComment ? 'bg-sky-500' : isPending ? 'bg-amber-400' : 'bg-green-400'}`} />

      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-800 truncate text-sm">
          {journal.title || 'Untitled'}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {journal.word_count.toLocaleString()} words
          </span>
          <span>·</span>
          <span>submitted {timeAgo(journal.created_at)}</span>
        </div>
      </div>

      {/* Waiting badge (pending only) */}
      {isPending && journal.wait_seconds != null && (
        <div
          data-testid={`wait-badge-${journal.journal_id}`}
          className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 flex-shrink-0"
        >
          <Timer className="w-3 h-3" />
          waiting {formatWait(journal.wait_seconds)}
        </div>
      )}

      {/* New comment badge — resurfaced journal with an unseen follow-up */}
      {hasNewComment && (
        <div
          data-testid={`new-comment-badge-${journal.journal_id}`}
          className="flex items-center gap-1 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1 flex-shrink-0"
        >
          <MessageSquare className="w-3 h-3" />
          New Comment
        </div>
      )}

      {/* Reviewed badge */}
      {isReviewed && (
        <div className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1 flex-shrink-0">
          <CheckCircle2 className="w-3 h-3" />
          Reviewed
        </div>
      )}

      {/* Acknowledge button */}
      {isPending && (
        <Button
          variant="outline"
          size="sm"
          data-testid={`acknowledge-btn-${journal.journal_id}`}
          onClick={(e) => { e.stopPropagation(); onAcknowledge(journal.journal_id); }}
          className="rounded-full border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400 gap-1.5 text-xs h-7 px-3 flex-shrink-0"
        >
          <ThumbsUp className="w-3 h-3" />
          Acknowledge
        </Button>
      )}

      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 transition-colors" />
    </div>
  );
}

// ─── Student status badge helper ─────────────────────────────────────────────

const getStatusBadge = (status) => {
  switch (status) {
    case 'draft':
      return <Badge variant="outline" className="text-slate-500"><Edit3 className="w-3 h-3 mr-1" /> Draft</Badge>;
    case 'submitted':
      return <Badge className="bg-amber-500 hover:bg-amber-500"><Clock className="w-3 h-3 mr-1" /> Pending Review</Badge>;
    case 'reviewed':
      return <Badge className="bg-green-600 hover:bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" /> Reviewed</Badge>;
    default:
      return null;
  }
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Journals({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Teacher state
  const [studentGroups, setStudentGroups] = useState([]);
  const [expandedStudents, setExpandedStudents] = useState(new Set());

  // Student state
  const [journals, setJournals] = useState([]);

  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [journalToDelete, setJournalToDelete] = useState(null);
  const [highlightedJournal, setHighlightedJournal] = useState(null);
  const journalRefs = useRef({});

  useEffect(() => {
    if (user.role === 'teacher') {
      fetchByStudent();
    } else {
      fetchStudentJournals();
    }
  }, []);

  // Highlight from notification (student view)
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && !loading && journals.length > 0) {
      const tryScroll = (attempts = 0) => {
        const ref = journalRefs.current[highlightId];
        if (ref) {
          setHighlightedJournal(highlightId);
          ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => setHighlightedJournal(null), 2500);
        } else if (attempts < 5) {
          setTimeout(() => tryScroll(attempts + 1), 200);
        }
      };
      const timer = setTimeout(() => tryScroll(), 300);
      return () => clearTimeout(timer);
    }
  }, [searchParams, loading, journals]);

  const fetchByStudent = async () => {
    try {
      const res = await axios.get(`${API}/journals/by-student`);
      setStudentGroups(res.data);
      // Auto-expand students with pending journals or unseen new comments
      setExpandedStudents(new Set(res.data.filter(s => s.pending_count > 0 || s.new_comment_count > 0).map(s => s.student_id)));
    } catch (error) {
      console.error("Error fetching journals by student:", error);
      toast.error("Failed to load journals");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentJournals = async () => {
    try {
      const res = await axios.get(`${API}/journals`);
      setJournals(res.data);
    } catch (error) {
      console.error("Error fetching journals:", error);
      toast.error("Failed to load journals");
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeJournal = async (journalId) => {
    try {
      await axios.post(`${API}/journals/${journalId}/acknowledge`);
      toast.success("Journal acknowledged");
      setStudentGroups(prev => prev.map(s => ({
        ...s,
        pending_count: s.journals.some(j => j.journal_id === journalId && j.status === 'submitted')
          ? s.pending_count - 1
          : s.pending_count,
        journals: s.journals.map(j =>
          j.journal_id === journalId ? { ...j, status: 'reviewed', wait_seconds: null } : j
        ),
      })));
    } catch (error) {
      toast.error("Failed to acknowledge journal");
    }
  };

  const deleteJournal = async () => {
    if (!journalToDelete) return;
    try {
      await axios.delete(`${API}/journals/${journalToDelete}`);
      toast.success("Journal deleted");
      setJournals(prev => prev.filter(j => j.journal_id !== journalToDelete));
    } catch (error) {
      toast.error("Failed to delete journal");
    } finally {
      setJournalToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteClick = (e, journalId) => {
    e.preventDefault();
    e.stopPropagation();
    setJournalToDelete(journalId);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // ── Teacher Dashboard View ──────────────────────────────────────────────────
  if (user.role === 'teacher') {
    const totalPending = studentGroups.reduce((acc, s) => acc + s.pending_count, 0);
    const totalReviewed = studentGroups.reduce((acc, s) =>
      acc + s.journals.filter(j => j.status === 'reviewed').length, 0);

    const allExpanded = studentGroups.length > 0 && expandedStudents.size === studentGroups.length;

    const toggleExpandAll = () => {
      if (allExpanded) {
        setExpandedStudents(new Set());
      } else {
        setExpandedStudents(new Set(studentGroups.map(s => s.student_id)));
      }
    };

    const toggleStudent = (studentId) => {
      setExpandedStudents(prev => {
        const next = new Set(prev);
        if (next.has(studentId)) next.delete(studentId);
        else next.add(studentId);
        return next;
      });
    };

    return (
      <Layout user={user}>
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-amber-600" />
                Student Journals
              </h1>
              <p className="text-slate-500 mt-1 text-sm">
                Review submissions by student — pending reviews sorted by longest wait.
              </p>
            </div>
            {studentGroups.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                data-testid="expand-collapse-all-btn"
                onClick={toggleExpandAll}
                className="rounded-full flex-shrink-0 mt-1 gap-1.5 text-slate-600 border-slate-200 hover:bg-slate-50"
              >
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${allExpanded ? 'rotate-180' : ''}`} />
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            )}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Users className="w-4 h-4 text-slate-400" />
              </div>
              <div className="text-2xl font-bold text-slate-900">{studentGroups.length}</div>
              <div className="text-xs text-slate-500 mt-0.5">Students</div>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 shadow-sm text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-2xl font-bold text-amber-700">{totalPending}</div>
              <div className="text-xs text-amber-600 mt-0.5">Pending Review</div>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 shadow-sm text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              </div>
              <div className="text-2xl font-bold text-green-700">{totalReviewed}</div>
              <div className="text-xs text-green-600 mt-0.5">Reviewed</div>
            </div>
          </div>

          {/* Student Cards */}
          {studentGroups.length === 0 ? (
            <Card className="border-slate-100">
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No journals submitted yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="student-journal-groups">
              {studentGroups.map(student => (
                <StudentJournalCard
                  key={student.student_id}
                  student={student}
                  isExpanded={expandedStudents.has(student.student_id)}
                  onToggle={() => toggleStudent(student.student_id)}
                  onAcknowledge={acknowledgeJournal}
                  onNavigate={(id) => navigate(`/journals/${id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ── Student View ────────────────────────────────────────────────────────────
  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-amber-600" />
              My Journals
            </h1>
            <p className="text-slate-600 mt-1">Record your spiritual journey and reflections</p>
          </div>
          <Button
            onClick={() => navigate('/journals/new')}
            className="bg-amber-600 hover:bg-amber-700 rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Entry
          </Button>
        </div>

        {journals.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Start your journaling journey</p>
              <Button
                onClick={() => navigate('/journals/new')}
                className="mt-4 bg-amber-600 hover:bg-amber-700 rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Write Your First Entry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {journals.map((journal) => {
              const isHighlighted = highlightedJournal === journal.journal_id;
              return (
                <Card
                  key={journal.journal_id}
                  ref={el => journalRefs.current[journal.journal_id] = el}
                  className={`border-slate-100 hover:border-slate-200 transition-all duration-300 cursor-pointer group ${isHighlighted ? 'notification-highlight ring-2 ring-amber-400 ring-offset-2' : ''}`}
                  onClick={() => navigate(`/journals/${journal.journal_id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {journal.title || 'Untitled'}
                          </h3>
                          {getStatusBadge(journal.status)}
                          {journal.has_new_comment && (
                            <Badge
                              data-testid={`new-comment-badge-${journal.journal_id}`}
                              className="bg-sky-500 hover:bg-sky-500 text-white gap-1"
                            >
                              <MessageSquare className="w-3 h-3" /> New Comment
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {format(new Date(journal.created_at), 'MMMM d, yyyy')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleDeleteClick(e, journal.journal_id)}
                          data-testid={`delete-journal-btn-${journal.journal_id}`}
                          className="h-8 w-8 rounded-full hover:bg-red-100 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={deleteJournal}
        title="Delete Journal?"
        description="This will permanently delete this journal entry. This action cannot be undone."
        confirmText="Delete Journal"
      />
    </Layout>
  );
}
